import { useEffect, useState } from 'react';
import { useSiteContext, useSnackbar } from 'src/store';

import { AppLogoImage, AppScreenshotImage } from '@orcha/micro-library-core-ui';

import Image from 'next/image';
import { logEventAnalytic } from 'src/utilities/logAnalytics';
import { stringSlugifier } from '@orcha/utils';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useAppStatus } from './AppCardWrapper.hooks';
import { AppCardWrapperProps } from './AppCardWrapper.types';
import DownloadDialog from '../../DownloadDialog';

const fallbackImageURL = '/app_screenshot_fallback.svg';
const imageFallBackUrl = '/app_image_fallback.png';
const fallbackLogoImageURL = '/app_logo_fallback.jpg';

const AppCardWrapper = ({
  appData,
  hideImages,
  AppCardComponent,
}: AppCardWrapperProps) => {
  const { t } = useTranslation('appCard');
  const { setSnackbarState } = useSnackbar();
  const router = useRouter();
  const {
    appId,
    name,
    logoUrl,
    cost,
    healthCategories,
    platformData,
    screenshotImageUrls,
    appStatus,
    assured,
  } = appData;

  const appNameSlugified = stringSlugifier(name);
  const appDetailsUrl = `/app/${appNameSlugified}/${appId.toLowerCase()}`;

  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [hostUrl, setHostUrl] = useState<string>();
  const { appCardConfig, showCommissionableStatus } = useSiteContext();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { host } = window.location;
      setHostUrl(host);
    }
  }, []);

  const handleDownloadClick = () => {
    setIsDownloadDialogOpen(true);
  };

  const handleCardClick = () => {
    logEventAnalytic({
      category: 'AppCard',
      action: 'Open app details - microsite',
      label: `${appId} - ${name}`,
    });
    router.push(appDetailsUrl);
  };

  const handleGetLinkClick = (success: boolean) => {
    setSnackbarState({
      severity: success ? 'success' : 'error',
      content: success
        ? t('appCommon:getLinkConfirmationMessage')
        : t('appCommon:getLinkErrorMessage'),
      isOpen: true,
    });
  };

  const filteredAppStatus = useAppStatus(appStatus, showCommissionableStatus);

  return (
    <>
      {isDownloadDialogOpen && (
        <DownloadDialog
          app={{
            appId,
            name,
            logoUrl,
            cost,
            appStatus: filteredAppStatus,
            platforms: platformData
              .filter((x) => x.platformName)
              .map((p) => {
                return {
                  platformName: p.platformName,
                  downloadLink: p.downloadLink,
                };
              }),
          }}
          open={isDownloadDialogOpen}
          handleClose={() => setIsDownloadDialogOpen(false)}
        />
      )}

      <AppCardComponent
        {...appCardConfig?.appCardTypeSettings}
        onClick={handleCardClick}
        title={name}
        FlexibleImageComponent={Image}
        avatarImage={
          !hideImages &&
          logoUrl && (
            <AppLogoImage
              FlexibleImageComponent={Image}
              fallbackLogoImageURL={fallbackLogoImageURL}
              appName={name}
              key={`${logoUrl}-${name}`}
              appLogoUrl={logoUrl}
              quality={50}
              priority
              width="40"
              height="40"
            />
          )
        }
        downloadButton={{
          onClick: () => {
            logEventAnalytic({
              category: 'Download',
              action: 'Open download modal from button - microsite',
              label: `${appId} - ${name}`,
            });
            handleDownloadClick();
          },
          label: t('downloadButton'),
        }}
        menuOptions={{
          openTitle: t('menu.openButtonTitle'),
          appDetails: {
            onClick: () => {
              logEventAnalytic({
                category: 'AppCard',
                action: 'Open details from dropdown - microsite',
                label: `${appId} - ${name}`,
              });
              router.push(appDetailsUrl);
            },
            label: t('menu.appDetailsLabel'),
          },
          getLink: {
            onClick: handleGetLinkClick,
            link: hostUrl + appDetailsUrl,
            label: t('menu.getLinkLabel'),
          },
          download: {
            onClick: () => {
              logEventAnalytic({
                category: 'Download',
                action: 'Open download modal from dropdown - microsite',
                label: `${appId} - ${name}`,
              });
              handleDownloadClick();
            },
            label: t('menu.downloadLabel'),
          },
        }}
        cost={cost}
        assured={assured}
        platforms={platformData
          ?.filter((p) => p.platformName)
          .map((p) => {
            const { platformName, level, score, levelFurtherInfoUrl } = p;
            return {
              platformName,
              platformTitle: platformName
                ? t(`appCommon:platformLabels.${platformName}` as const)
                : '',
              score: score ? Math.round(score) : undefined,
              ...(level && {
                level: t('level.label', {
                  level,
                }),
              }),
              ...(levelFurtherInfoUrl && {
                levelFurtherInfo: t('level.explanation', {
                  linkTarget: levelFurtherInfoUrl,
                }),
              }),
            };
          })}
        categories={healthCategories}
        appStatus={filteredAppStatus}
        appImages={{
          imageElements: screenshotImageUrls
            ?.filter((x) => x.trim() !== '')
            ?.map((imageSrc: string, index: number) => {
              const url = new URL(imageSrc);
              return hideImages ? (
                <></>
              ) : (
                <AppScreenshotImage
                  FlexibleImageComponent={Image}
                  fallbackImageURL={fallbackImageURL}
                  alt={`screenshot ${index + 1}`}
                  src={url.toString()}
                />
              );
            }),
          fallbackImageElement: hideImages ? (
            <></>
          ) : (
            <Image
              src={imageFallBackUrl}
              alt=""
              height="165"
              width="298"
              quality={50}
              priority
            />
          ),
        }}
        translations={{
          commissionedCostTypeText: t('microsite:appCostLabels.Commissioned'),
          commissionableCostTypeText: t(
            'microsite:appCostLabels.Commissionable'
          ),
          commissionedFooterText: t(
            'microsite:appCard.footerLabels.Commissioned'
          ),
          commissionableFooterText: t(
            'microsite:appCard.footerLabels.Commissionable'
          ),
          freeTrialPeriodCostTypeText: t(
            'microsite:appCostLabels.FreeTrialPeriod'
          ),
          freeTrialPeriodFooterText: t(
            'microsite:appCard.footerLabels.FreeTrialPeriod'
          ),
          discountedCostFooterText: t(
            'microsite:appCard.footerLabels.DiscountedCost'
          ),
          discountedCostTypeText: t('microsite:appCostLabels.DiscountedCost'),
        }}
      />
    </>
  );
};

export default AppCardWrapper;
